if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.querySelector('.hawthorn-lighting, .hawthorn-set')) {
          this.addEventListener('change', (event) => {
            if (event.target.matches('[data-hawthorn-set-toggle]')) {
              const setLength = this.querySelector('.hawthorn-set__length');
              if (setLength) setLength.hidden = !event.target.checked;
            }
            this.updateSelectionTotal();
          });
          this.updateSelectionTotal();
        }
      }

      connectedCallback() {
        if (!this.querySelector('.hawthorn-lighting, .hawthorn-set')) return;
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.sectionId === this.dataset.sectionId) this.updateSelectionTotal();
        });
        this.updateSelectionTotal();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
      }

      getSelectedSetVariant() {
        const setFieldset = this.querySelector('.hawthorn-set');
        if (!setFieldset?.querySelector('[data-hawthorn-set-toggle]:checked')) return null;
        try {
          const wreathVariants = JSON.parse(setFieldset.dataset.wreathVariants || '[]');
          const setVariants = JSON.parse(setFieldset.dataset.setVariants || '[]');
          const wreathVariant = wreathVariants.find((variant) => variant.id === Number(this.variantIdInput.value));
          const size = wreathVariant?.option1 || wreathVariant?.options?.[0];
          const length = setFieldset.querySelector('[data-hawthorn-set-length]')?.value;
          return setVariants.find((variant) => variant.option1 === size && variant.option2 === length && variant.available) || null;
        } catch {
          return null;
        }
      }

      syncLightingChoice() {
        const input = this.querySelector('[data-single-light-variant]');
        if (!input) return;
        const isSet = !!this.querySelector('[data-hawthorn-set-toggle]:checked');
        input.dataset.hawthornLightVariant = isSet ? input.dataset.setLightVariant : input.dataset.singleLightVariant;
        input.dataset.hawthornLightPrice = isSet ? input.dataset.setLightPrice : input.dataset.singleLightPrice;
        input.dataset.hawthornLightPiece = isSet ? 'Both pieces' : 'Wreath';
        input.disabled = !input.dataset.hawthornLightVariant;
        if (input.disabled) input.checked = false;
        const label = this.querySelector('[data-hawthorn-light-label]');
        const amount = this.querySelector('[data-hawthorn-light-label-price]');
        if (label) label.textContent = isSet ? 'Light both pieces' : 'Light the wreath';
        if (amount) amount.textContent = '+' + new Intl.NumberFormat('en-GB', {style:'currency', currency:'GBP'}).format(Number(input.dataset.hawthornLightPrice || 0) / 100);
      }

      updateSelectionTotal() {
        this.syncLightingChoice();
        const lighting = this.querySelector('.hawthorn-lighting');
        const setFieldset = this.querySelector('.hawthorn-set');
        const metadata = lighting || setFieldset;
        if (!metadata) return;
        let basePrice = Number(metadata.dataset.basePrice) || 0;
        try {
          const variants = JSON.parse(lighting?.dataset.productVariants || setFieldset?.dataset.wreathVariants || '[]');
          const variant = variants.find((item) => item.id === Number(this.variantIdInput.value));
          if (variant) basePrice = Number(variant.price);
        } catch {
          // Preserve the initially rendered price if variant metadata is unavailable.
        }
        const setVariant = this.getSelectedSetVariant();
        const setPrice = setFieldset?.querySelector('[data-hawthorn-set-price]');
        if (setPrice) {
          setPrice.textContent = setVariant
            ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(setVariant.price) / 100)
            : setPrice.dataset.defaultPrice;
        }
        if (setVariant) basePrice = Number(setVariant.price);
        const upgrades = Array.from(this.querySelectorAll('[data-hawthorn-light-variant]:checked'))
          .reduce((total, input) => total + Number(input.dataset.hawthornLightPrice || 0), 0);
        const total = this.querySelector('[data-hawthorn-selection-total]');
        if (total) {
          total.textContent = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
            .format((basePrice + upgrades) / 100);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.syncLightingChoice();
        const selectedLights = Array.from(this.querySelectorAll('[data-hawthorn-light-variant]:checked'));
        const setSelected = !!this.querySelector('[data-hawthorn-set-toggle]:checked');
        const setVariant = this.getSelectedSetVariant();
        if (setSelected && !setVariant) {
          this.handleErrorMessage('This matching set is unavailable for the selected size and garland length.');
          return;
        }
        if (selectedLights.length || setVariant) {
          this.addConfiguredItems(selectedLights, setVariant);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async addConfiguredItems(selectedLights, setVariant = null) {
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = Number(setVariant?.id || formData.get('id'));
        const quantity = Number.parseInt(formData.get('quantity'), 10) || 1;
        const lightingFieldset = this.querySelector('.hawthorn-lighting');
        const setFieldset = this.querySelector('.hawthorn-set');
        const productTitle = setVariant ? setFieldset?.dataset.setTitle || '' : lightingFieldset?.dataset.productTitle || '';
        let variantDescription = '';
        if (setVariant) {
          variantDescription = setVariant.public_title || setVariant.title || '';
        } else {
          try {
            const variants = JSON.parse(lightingFieldset?.dataset.productVariants || '[]');
            const selectedVariant = variants.find((variant) => variant.id === variantId);
            variantDescription = selectedVariant?.public_title || selectedVariant?.title || '';
          } catch {
            // The upgrade still carries the product title if variant metadata is unavailable.
          }
        }
        const itemDescription = variantDescription ? `${productTitle} (${variantDescription})` : productTitle;
        const group = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const properties = selectedLights.length ? { '_Hawthorn set reference': group } : {};
        const coreVariants = JSON.parse(lightingFieldset?.dataset.productVariants || '[]');
        const coreSku = setVariant?.sku || coreVariants.find((variant) => variant.id === variantId)?.sku;
        if (selectedLights.length && coreSku) properties['_Hawthorn configuration SKU'] = `${coreSku}-LIT`;
        for (const [key, value] of formData.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match) properties[match[1]] = value;
        }
        const items = [];
        for (const light of selectedLights) {
          items.push({
            id: Number(light.dataset.hawthornLightVariant),
            quantity,
            properties: {
              '_Hawthorn set reference': group,
              ...(properties['_Hawthorn configuration SKU'] ? {'_Hawthorn configuration SKU': properties['_Hawthorn configuration SKU']} : {}),
              'Added to': `${itemDescription} — ${light.dataset.hawthornLightPiece}`,
            },
          });
        }
        items.push({ id: variantId, quantity, properties });

        try {
          if (!Number.isSafeInteger(variantId) || items.some((item) => !Number.isSafeInteger(item.id) || item.id <= 0)) {
            throw new Error('Please choose a valid product option.');
          }
          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items }),
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add these items to your cart.');
          }
          window.location.assign(routes.cart_url);
        } catch (error) {
          this.handleErrorMessage(error.message || 'Unable to add these items to your cart.');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
